import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import axios from 'axios'
import puppies from '../data/puppies'
import './PuppyInquiry.css'

const PuppyInquiry = () => {
  const { puppyId } = useParams()
  const navigate = useNavigate()
  const [puppy, setPuppy] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('') // Store email for success message
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    message: '',
    experience: '',
    livingSpace: '',
    hasOtherPets: 'no',
    deliveryMethod: 'pickup',
    timeframe: ''
  })

  useEffect(() => {
    const foundPuppy = puppies.find(p => p.id === parseInt(puppyId))
    if (foundPuppy) {
      setPuppy(foundPuppy)
    } else {
      // Redirect to home if puppy not found
      navigate('/')
    }
  }, [puppyId, navigate])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const adminResponse = await axios.post('/api/send-email', { formData, puppy });

      const confirmationResponse = await axios.post('/api/send-confirmation', {
        customerEmail: formData.email,
        customerName: formData.name,
        puppyName: puppy.name
      });

  setSubmittedEmail(formData.email)
  setSubmitted(true)
  
  // Scroll to top to show the thank you message
  window.scrollTo({ top: 0, behavior: 'smooth' })
  
  setFormData({ name: '', email: '', phone: '', location: '', message: '', experience: '', livingSpace: '', hasOtherPets: 'no', deliveryMethod: 'pickup', timeframe: '' })
    } catch (error) {
      const serverData = error.response?.data
      console.error('Failed to send emails:', error, 'Server:', serverData)
      alert(`Failed to send inquiry. Server says: ${serverData?.error || error.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!puppy) {
    return (
      <div className="loading-container">
        <motion.div
          className="loading-spinner"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          🐕
        </motion.div>
      </div>
    )
  }

  if (submitted) {
    return (
      <motion.div
        className="inquiry-success"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container">
          <div className="success-content">
            <motion.div
              className="success-icon"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
            >
              ✅
            </motion.div>
            <h1>Thank You for Your Inquiry!</h1>
            <p>
              We've received your inquiry about <strong>{puppy.name}</strong> and will get back to you within 24 hours with more information.
            </p>
            <p>
              📧 <strong>A confirmation email has been sent to {submittedEmail}</strong>
            </p>
            <div className="confirmation-details">
              <p><strong>What happens next?</strong></p>
              <ul>
                <li>📧 We'll review your inquiry and respond within 24 hours</li>
                <li>📞 You can also reach us directly at info@majesticcorsokennels.com</li>
                <li>🐕 We'll provide additional photos and information about {puppy.name}</li>
                <li>📅 We'll discuss scheduling a visit or video call</li>
              </ul>
            </div>
            <motion.button
              className="btn btn-primary"
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Return to Home
            </motion.button>
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="puppy-inquiry">
      <div className="container">
        <motion.div
          className="inquiry-header"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.button
            className="back-button"
            onClick={() => navigate('/')}
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H6m6-7l-7 7 7 7"/>
            </svg>
            Back to Puppies
          </motion.button>
          <h1>Inquire About {puppy.name}</h1>
          <p>Fill out the form below to learn more about this beautiful Cane Corso puppy.</p>
        </motion.div>

        <div className="inquiry-content">
          <motion.div
            className="puppy-showcase"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="puppy-image-container">
              <img src={puppy.image} alt={puppy.name} className="puppy-image" />
              <div className={`status-badge ${puppy.status.toLowerCase()}`}>
                {puppy.status}
              </div>
            </div>
            <div className="puppy-details">
              <h3>{puppy.name}</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="label">Gender:</span>
                  <span className="value">{puppy.gender}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Age:</span>
                  <span className="value">{puppy.age}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Registration:</span>
                  <span className="value">{puppy.registration}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Breed:</span>
                  <span className="value">{puppy.breed}</span>
                </div>
                {/* Weight removed as per request */}
                <div className="detail-item">
                  <span className="label">Price:</span>
                  <span className="value price">{puppy.price}</span>
                </div>
              </div>
              <p className="puppy-description">{puppy.description}</p>
              {puppy.temperament && (
                <div className="temperament-tags">
                  {puppy.temperament.split(', ').map((trait, i) => (
                    <span key={i} className="temperament-tag">{trait}</span>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            className="inquiry-form-container"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <form className="inquiry-form" onSubmit={handleSubmit}>
              <h3>Contact Information</h3>
              
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="location">Location *</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="City, State (e.g., Sacramento, CA)"
                    required
                  />
                </div>
              </div>

              <h3>About Your Family</h3>

              <div className="form-group">
                <label htmlFor="experience">Experience with Large Dogs</label>
                <select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                >
                  <option value="">Please select...</option>
                  <option value="first-time">First-time owner</option>
                  <option value="some">Some experience</option>
                  <option value="experienced">Very experienced</option>
                  <option value="cane-corso">Previous Cane Corso owner</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="livingSpace">Living Situation</label>
                <select
                  id="livingSpace"
                  name="livingSpace"
                  value={formData.livingSpace}
                  onChange={handleInputChange}
                >
                  <option value="">Please select...</option>
                  <option value="apartment">Apartment</option>
                  <option value="house-small-yard">House with small yard</option>
                  <option value="house-large-yard">House with large yard</option>
                  <option value="farm-acreage">Farm/Acreage</option>
                </select>
              </div>

              <div className="form-group">
                <label>Do you have other pets?</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="hasOtherPets"
                      value="no"
                      checked={formData.hasOtherPets === 'no'}
                      onChange={handleInputChange}
                    />
                    <span>No</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="hasOtherPets"
                      value="yes"
                      checked={formData.hasOtherPets === 'yes'}
                      onChange={handleInputChange}
                    />
                    <span>Yes</span>
                  </label>
                </div>
              </div>

              <h3>Pickup or Delivery</h3>
              <div className="form-group">
                <label>How would you like to get your puppy?</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="deliveryMethod"
                      value="pickup"
                      checked={formData.deliveryMethod === 'pickup'}
                      onChange={handleInputChange}
                    />
                    <span>Pickup</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="deliveryMethod"
                      value="delivery"
                      checked={formData.deliveryMethod === 'delivery'}
                      onChange={handleInputChange}
                    />
                    <span>Delivery</span>
                  </label>
                </div>
              </div>

              <h3>Preferred Timing</h3>
              <div className="form-group">
                <label>When would you like to have the puppy?</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="timeframe"
                      value="right away"
                      checked={formData.timeframe === 'right away'}
                      onChange={handleInputChange}
                    />
                    <span>Right away</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="timeframe"
                      value="within a week"
                      checked={formData.timeframe === 'within a week'}
                      onChange={handleInputChange}
                    />
                    <span>Within a week</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="timeframe"
                      value="not certain"
                      checked={formData.timeframe === 'not certain'}
                      onChange={handleInputChange}
                    />
                    <span>Not certain</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="timeframe"
                      value="just looking"
                      checked={formData.timeframe === 'just looking'}
                      onChange={handleInputChange}
                    />
                    <span>Just looking</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={`Tell us why you're interested in ${puppy.name} and any questions you have...`}
                  rows="5"
                  required
                />
              </div>

              <motion.button
                type="submit"
                className="btn btn-primary btn-lg submit-btn"
                disabled={isSubmitting}
                whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
              >
                {isSubmitting ? (
                  <>
                    <motion.div
                      className="spinner"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Sending Inquiry...
                  </>
                ) : (
                  <>
                    Send Inquiry
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                    </svg>
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default PuppyInquiry
